import { Box } from "@mui/material";
import { NewOrderLong } from "../newOrder/long/NewOrderLong";
import { FormValueTypes } from "../types/longOrderTypes";
import OrdersHeader from "../OrdersHeader";
import { useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  fetchOrderRowsByBundleId,
  selectOrderRowById,
} from "../orderRowsDetailedSlice";
import { useNavigate, useParams } from "react-router-dom";
import { nanoid } from "@reduxjs/toolkit";
import { shortOrderDefaultFieldValues } from "../orderTypes";
import {
  fetchOrderTemplates,
  selectActiveOrderTemplates,
} from "../../customer/orderTemplatesSlice";
import {
  fetchWorkLocationWithOrgById,
  selectAllCustomerWorkLocations,
} from "../../customer/customerWorkLocationsSlice";
import { AccessRightContext } from "../../user/AccessRightProvider";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { toFormFieldRules } from "../utils";

export const CopyLongOrder = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { bundleId } = useParams();

  const { canReadOrder } = useContext(AccessRightContext);

  const [name, setName] = useState("");
  const [orderRowCount, setOrderRowCount] = useState<number>(0);

  const order = useAppSelector((state) =>
    selectOrderRowById(state, bundleId!!),
  );

  const orderRowTemplates = useAppSelector(selectActiveOrderTemplates);
  const orderRowTemplate = orderRowTemplates.find(
    (template) => template.id === order?.orderRowTemplateId,
  );
  const workLocation = useAppSelector((state) =>
    selectAllCustomerWorkLocations(state),
  ).find((location) => location.id === order?.workLocationId);

  const localized = useAppSelector((state) => state.localized);
  const { skill, training, certificate } = localized;

  const defaultValues: FormValueTypes = {
    edit: false,
    uid: nanoid(),
    isReadOnly: false,
    title: "",
    customerOrgId: order?.customerOrgId ?? "",
    type: order?.type,
    workLocation: {
      ...workLocation,
      id: workLocation?.id ?? "",
      name: workLocation?.name ?? "",
      contractId: workLocation?.contractId ?? "",
    },
    orderTemplate: {
      ...shortOrderDefaultFieldValues.orderTemplate,
      id: order?.orderRowTemplateId ?? "",
      name: order?.orderRowTemplateName ?? "",
      workTitleId: order?.taskSpec.workTitleId ?? "",
      tesId: orderRowTemplate?.tesId ?? "",
      periodStartDate: orderRowTemplate?.periodStartDate
        ? new Date(orderRowTemplate.periodStartDate)
        : null,
      supplements: orderRowTemplate?.supplements ?? {},
      supplementPriceOverrides:
        orderRowTemplate?.supplementPriceOverrides ?? {},
    },
    amount: order?.amount ?? 1,
    customerCostCenters:
      order?.customerCostCenterIds.map((id) => ({
        customerCCId: id,
        id,
        name:
          workLocation?.customerCCs.find((cc) => cc.customerCCId === id)
            ?.name ?? "",
      })) || [],
    reasonForTemporaryEmployment: order?.reasonForTemporaryEmployment ?? "",
    workStarts: null,
    workEnds: null,
    shortWorkTimeDescriptionFi: order?.shortWorkTimeDescriptionFi ?? "",
    shortWorkTimeDescriptionEn: order?.shortWorkTimeDescriptionEn ?? "",
    longWorkTimeDescriptionFi: order?.longWorkTimeDescriptionFi ?? "",
    longWorkTimeDescriptionEn: order?.longWorkTimeDescriptionEn ?? "",
    workDescriptionFi: order?.taskSpec.description ?? "",
    workDescriptionEn: order?.taskSpec.descriptionEn ?? "",
    arrivalInstructionsFi: order?.taskSpec.descriptionForTalent ?? "",
    arrivalInstructionsEn: order?.taskSpec.descriptionForTalentEn ?? "",
    languages: order?.taskSpec.languageSkills ?? [],
    certificates:
      order?.taskSpec.certificateIds.map((id) => ({
        id,
        name: certificate[id],
      })) ?? [],
    skills:
      order?.taskSpec.skillIds.map((id) => ({ id, name: skill[id] })) ?? [],
    trainings:
      order?.taskSpec.trainings.map((id) => ({ id, name: training[id] })) ?? [],
    tesVariation: order?.taskSpec.tesVariation ?? [],
    descriptionToEezyAndCustomer: order?.descriptionToEezyAndCustomer ?? "",
    targetedShiftTargetingRules: toFormFieldRules(
      order?.targetedShiftTargetingRules,
      localized,
    ),
  };
  // Check if user is allowed to read
  useEffect(() => {
    if (order && !canReadOrder(order.eezyCompanyId)) {
      navigate("/home/orders");
    }
  }, [order, canReadOrder, navigate]);

  // Fetch order data if needed
  useEffect(() => {
    if (order) return;
    dispatch(fetchOrderRowsByBundleId({ id: bundleId!! }));
  }, [bundleId, order, canReadOrder, navigate, dispatch]);

  // Fetch order templates if needed
  useEffect(() => {
    if (orderRowTemplates || !order) return;
    dispatch(fetchOrderTemplates({ contractId: order.contractId }));
  }, [orderRowTemplates, order, dispatch]);

  // Fetch work location if needed
  useEffect(() => {
    if (workLocation || !order) return;
    dispatch(
      fetchWorkLocationWithOrgById({
        workLocationId: order.workLocationId,
        customerOrgId: order.customerOrgId,
      }),
    );
  }, [workLocation, order, dispatch]);

  const handleOrderTitleChange = (name: string) => setName(name);

  return !order || !orderRowTemplates || !workLocation ? (
    <OverlaySpinner isLoading />
  ) : (
    <Box sx={{ maxWidth: "1700px" }}>
      <OrdersHeader
        title={name}
        handleOrderTitleChange={handleOrderTitleChange}
        customerCompany={order?.customerOrgName ?? ""}
        orderRowCount={orderRowCount}
      />
      <NewOrderLong
        title={name}
        defaultValues={defaultValues}
        updateOrderRowCount={(count) => setOrderRowCount(count)}
      />
    </Box>
  );
};
